import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Send, UploadCloud, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { createSupportTicket } from '../apis/supportService';
import { sessionService } from '../apis/sessionService';
import { useAuth } from '../context/AuthContext';
import { useTrial } from '../context/TrialContext';
import { hasPriorityFeatureRequests } from '../utils/accountLimitsUtils';

const initialFormState = {
  topic: 'product-support',
  priority: 'medium',
  title: '',
  message: '',
  isAssignedSOAPNote: false,
};

const SupportTicketPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]); // File[]
  const [previews, setPreviews] = useState([]); // [{url,name,size,id}]
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_ATTACHMENTS = 10;
  const MAX_BYTES = 20 * 1024 * 1024; // 20MB

  // Get user info from auth context
  const { user } = useAuth();
  const { trialStatus } = useTrial();
  const elitePriority = hasPriorityFeatureRequests(trialStatus);
  const isAdmin = user?.role === 'admin'; // Adjust based on user role structure
  const shouldHideButtons = !isAdmin || formData.isAssignedSOAPNote;

  const resetForm = () => {
    const topicParam = searchParams.get('topic');
    setFormData(topicParam ? { ...initialFormState, topic: topicParam } : initialFormState);
    setAttachments([]);
    setPreviews((prev) => {
      prev.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
      return [];
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const topicParam = searchParams.get('topic');
    if (topicParam) {
      setFormData((prev) => ({
        ...prev,
        topic: topicParam,
        ...(elitePriority && topicParam === 'feature-request'
          ? { priority: 'high' }
          : {}),
      }));
    }
  }, [searchParams, elitePriority]);

  useEffect(() => {
    if (!elitePriority) return;
    if (formData.topic === 'feature-request' && formData.priority === 'medium') {
      setFormData((prev) => ({ ...prev, priority: 'high' }));
    }
  }, [elitePriority, formData.topic, formData.priority]);


  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Image attachments logic
  const addFiles = (incoming) => {
    const files = Array.from(incoming || []);
    if (!files.length) return;

    const filtered = [];
    for (const f of files) {
      if (!f.type || !f.type.startsWith('image/')) {
        toast.error(`${f.name}: only image files are allowed`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: must be under 20MB`);
        continue;
      }
      filtered.push(f);
    }

    if (!filtered.length) return;

    const keyOf = (f) => `${f.name}|${f.size}|${f.lastModified}`;
    const existing = attachments || [];
    const existingKeys = new Set(existing.map(keyOf));
    const merged = [...existing];
    for (const f of filtered) {
      const k = keyOf(f);
      if (!existingKeys.has(k)) {
        merged.push(f);
        existingKeys.add(k);
      }
    }

    if (merged.length > MAX_ATTACHMENTS) {
      toast.error(`You can attach up to ${MAX_ATTACHMENTS} images.`);
    }
    setAttachments(merged.slice(0, MAX_ATTACHMENTS));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    addFiles(event.dataTransfer?.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const removeAttachmentAt = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    // Cleanup previous urls
    setPreviews((prev) => {
      prev.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
      return [];
    });

    if (!attachments || attachments.length === 0) return;
    const next = attachments.map((f, idx) => ({
      url: URL.createObjectURL(f),
      name: f.name,
      size: f.size,
      id: `${f.name}-${f.size}-${f.lastModified}-${idx}`,
    }));
    setPreviews(next);

    return () => {
      next.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  const handleFilesChange = (event) => {
    addFiles(event.target.files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await createSupportTicket({
        title: formData.title,
        message: formData.message,
        category: formData.topic,
        priority: formData.priority,
        attachments,
      });

      toast.success('Thanks! Your request is on its way to our support team.');
      resetForm();
      navigate(isAdmin ? '/scribe/settings?tab=track-tickets' : '/scribe/settings?tab=support-tickets');
    } catch (error) {
      const message = error?.response?.data?.message || (typeof error?.response?.data?.error === 'string' ? error.response.data.error : null) || 'We could not submit your ticket. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.35),_transparent_55%)]" />

      {/* Floating back button (top-left) */}
      <div className="fixed left-4 top-5 z-30 flex flex-col gap-3 sm:left-8 sm:top-6">
        <button
          type="button"
          onClick={() => navigate("/scribe/settings")}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur hover:bg-white/20"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Tickets
        </button>

        {/* Conditionally render these two buttons */}
        {!shouldHideButtons && (
          <>
            <button
              type="button"
              onClick={async () => {
                try {
                  const activeSession = await sessionService.getOrCreateActiveSession();
                  const sessionId =
                    activeSession?.data?.session?.sessionId ||
                    activeSession?.body?.session?.sessionId ||
                    activeSession?.session?.sessionId ||
                    activeSession?.data?.sessionId ||
                    activeSession?.sessionId;

                  if (sessionId) {
                    navigate(`/scribe?sessionId=${sessionId}`);
                  } else {
                    // Fallback to scribe home; it will load/create a session
                    navigate("/scribe");
                    toast.error("No active session found");
                  }
                } catch (err) {
                  const limitMessage =
                    err?.errorCode === "CHART_LIMIT_REACHED" ||
                      err?.errorCode === "TRIAL_PERIOD_EXPIRED"
                      ? err.message
                      : "Unable to load current session";
                  toast.error(limitMessage);
                }
              }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur hover:bg-white/20"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Current Session
            </button>

            <button
              type="button"
              onClick={async () => {
                try {
                  const newSession = await sessionService.createSession({
                    sessionName: "New Support Session",
                    encounterType: "general",
                  });
                  const sessionId =
                    newSession?.data?.session?.sessionId ||
                    newSession?.body?.session?.sessionId ||
                    newSession?.data?.sessionId ||
                    newSession?.sessionId;

                  if (sessionId) {
                    navigate(`/scribe?sessionId=${sessionId}`);
                    toast.success("New session created");
                  } else {
                    toast.error("Session creation failed");
                  }
                } catch (err) {
                  const limitMessage =
                    err?.errorCode === "CHART_LIMIT_REACHED" ||
                      err?.errorCode === "TRIAL_PERIOD_EXPIRED"
                      ? err.message
                      : "Failed to create a new session";
                  toast.error(limitMessage);
                }
              }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur hover:bg-white/20"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to New Session
            </button>
          </>
        )}
      </div>

      <div
        className="relative mx-auto flex min-h-[calc(100vh-80px)] w-full max-w-5xl items-start px-4 pb-6 pt-12 sm:px-6 lg:px-8 lg:items-center lg:py-8"
      >
        <div className="grid w-full gap-4 sm:gap-6 rounded-2xl sm:rounded-3xl border border-white/10 bg-white/5 p-4 sm:p-6 shadow-[0_32px_96px_-48px_rgba(59,130,246,0.55)] backdrop-blur-lg lg:grid-cols-[0.85fr_1.15fr] lg:gap-8 lg:p-7">
          <div className="flex flex-col justify-between lg:min-h-[300px]">
            <div className="space-y-2.5 sm:space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-0.5 sm:px-3.5 sm:py-1 text-xs font-semibold uppercase tracking-wider text-blue-100/90 w-fit">
                Need help from VirtualScribe
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <h1 className="text-xl sm:text-3xl lg:text-4xl font-semibold leading-tight">Raise a support request</h1>
                <p className="text-xs sm:text-sm leading-relaxed text-blue-100/85">
                  Describe the issue, what you have tried so far, and the outcome you are expecting. A VirtualScribe support engineer will follow up within 2-3 business days.
                </p>
              </div>
            </div>
            <div className="hidden items-center gap-3 text-xs sm:text-sm text-blue-100/70 lg:flex mt-4">
              <a href="mailto:support@virtualscribe.ai" className="font-medium text-white hover:text-blue-200 transition-colors">
                Email: support@virtualscribe.ai
              </a>
            </div>
          </div>

          <div className="rounded-xl sm:rounded-2xl border border-slate-200/20 bg-white/95 p-4 sm:p-5.5 text-slate-700 shadow-xl shadow-blue-950/30">
            <form onSubmit={handleSubmit} className="grid gap-3 sm:gap-3.5">

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1">
                  <label htmlFor="topic" className="text-xs sm:text-sm font-semibold text-slate-800">
                    Topic
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="h-10 sm:h-10.5 w-full rounded-lg sm:rounded-xl border border-slate-300 bg-white pl-3.5 pr-8 text-xs sm:text-sm font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 shadow-xs"
                  >
                    <option value="product-support">Product support</option>
                    <option value="billing">Billing & accounts</option>
                    <option value="integrations">Integrations</option>
                    <option value="security">Security & compliance</option>
                    <option value="feature-request">Feature request</option>
                  </select>
                </div>
                <div className="grid gap-1">
                  <label htmlFor="priority" className="text-xs sm:text-sm font-semibold text-slate-800">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="h-10 sm:h-10.5 w-full rounded-lg sm:rounded-xl border border-slate-300 bg-white pl-3.5 pr-8 text-xs sm:text-sm font-medium text-slate-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 shadow-xs"
                  >
                    <option value="low">Low - general question</option>
                    <option value="medium">Medium - needs attention soon</option>
                    <option value="high">High - blocking workflow</option>
                    <option value="critical">Critical - clinical impact</option>
                  </select>
                  {elitePriority && formData.topic === 'feature-request' && (
                    <p className="text-xs sm:text-xs text-amber-700 font-medium">
                      Elite priority: feature requests are routed as High.
                    </p>
                  )}
                </div>
              </div>
              <div className="grid gap-1">

                <label htmlFor="title" className="text-xs sm:text-sm font-semibold text-slate-800">Title</label>

                <input
                  id="title"
                  name="title"
                  type="text"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Brief summary (e.g., 'error messages')"
                  disabled={isSubmitting}
                  className="h-10 sm:h-10.5 w-full rounded-lg sm:rounded-xl border border-slate-300 bg-white px-3.5 text-xs sm:text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 shadow-xs"
                  required
                />

              </div>
              <div className="grid gap-1">
                <label htmlFor="message" className="text-xs sm:text-sm font-semibold text-slate-800">
                  Details
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Include timelines, error messages, relevant IDs, and any context that helps us reproduce the issue."
                  rows={4}
                  required
                  className="resize-none text-xs sm:text-sm p-3 font-medium text-slate-900 leading-relaxed placeholder:text-slate-400 rounded-lg sm:rounded-xl border-slate-300 min-h-[105px] shadow-xs"
                  disabled={isSubmitting}
                />
                <div className="mt-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs sm:text-sm font-semibold text-slate-800" htmlFor="attachments">Attachments</label>
                    <div className="text-xs sm:text-xs text-slate-500 font-medium">{attachments.length}/{MAX_ATTACHMENTS} images</div>
                  </div>
                  <div
                    className={`mt-1 flex w-full flex-col items-center justify-center rounded-lg sm:rounded-xl border px-3 py-2.5 text-center transition ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-white'} ${isSubmitting ? 'opacity-60' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                    aria-disabled={isSubmitting}
                  >
                    <input
                      id="attachments"
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFilesChange}
                      disabled={isSubmitting}
                      className="hidden"
                    />
                    <UploadCloud className="h-5 w-5 text-slate-400" />
                    <div className="mt-0.5 text-xs sm:text-sm text-slate-700 font-medium">
                      Drag & drop images here, or <span className="text-blue-600 font-semibold underline">browse</span>
                    </div>
                    <div className="text-xs sm:text-xs text-slate-500">PNG, JPG, WEBP, GIF up to 20MB each</div>
                  </div>

                  {previews.length > 0 && (
                    <>
                      <div className="mt-2 mb-1 text-xs sm:text-xs font-medium text-slate-500">Selected images</div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                        {previews.map((p, idx) => (
                          <div key={p.id} className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                            <img src={p.url} alt={p.name} className="h-14 w-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeAttachmentAt(idx)}
                              className="absolute right-1 top-1 inline-flex h-4.5 w-4.5 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow hover:bg-white"
                              aria-label={`Remove ${p.name}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <div className="line-clamp-1 p-1 text-xs text-slate-600">{p.name}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-1.5 flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setAttachments([])}
                          disabled={isSubmitting}
                          className="rounded-full bg-white px-3 py-0.5 text-xs text-slate-700 hover:bg-slate-50 h-7"
                        >
                          Clear all
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 px-6 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-70 transition-all sm:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Send className="h-3.5 w-3.5 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" /> Submit ticket
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketPage;